import * as types from './types';
import { auth, clearTokenCache } from '../../../services/firebase';
import { signInWithEmailAndPassword, getIdToken } from 'firebase/auth';
import { log } from '../../../utils/jsUtils';
import { setSnackMessage } from '../common/actions';
import SPBackend from '../../../services/SPBackend';
import { getUserRole } from '../../../models/User';
import { setDefaultBuilding as setDefaultBuildingRedux } from '../../modules/buildings/actions';
import { setBilling } from '../../modules/billing/actions';
import { feedApi, tagsApi } from '../../../features/api';
import { setAuth, clearAuth, setAuthLoading } from '../auth/actions';
import { setTags as setTagsInTagsSlice } from '../tags/actions';

export const setUser = (user) => {
  // Firebase user object structure is different from our Redux structure
  // Extract needed fields for our Redux store
  return {
    type: types.SET_USER,
    payload: {
      uid: user.uid,
      email: user.email
    }
  };
};

export const setUserRole = (role) => ({
  type: types.SET_USER_ROLE,
  payload: role
});

export const setUserFullName = (fullName) => ({
  type: types.SET_USER_FULLNAME,
  payload: fullName
});

export const setDemoMode = (isDemoMode) => ({
  type: types.SET_DEMO_MODE,
  payload: isDemoMode
});

export const setAccounts = (accounts) => ({
  type: types.SET_ACCOUNTS,
  payload: accounts
});

export const setAccountId = (accountId) => ({
  type: types.SET_ACCOUNT_ID,
  payload: accountId
});

export const setCompanyName = (companyName) => ({
  type: types.SET_COMPANY_NAME,
  payload: companyName
});

export const setAccountSettings = (settings) => ({
  type: types.SET_ACCOUNT_SETTINGS,
  payload: settings
});

export const setAccountUsage = (usage) => ({
  type: types.SET_ACCOUNT_USAGE,
  payload: usage
});

// Note: setTags removed - tags are now managed in state.tags.tags via tags/actions.js

export const setDefaultUnit = (unit) => ({
  type: types.SET_DEFAULT_UNIT,
  payload: unit
});

export const setIsPhoneVerified = (isVerified) => ({
  type: types.SET_IS_PHONE_VERIFIED,
  payload: isVerified
});

// Batched action to set entire user profile at once (reduces re-renders)
export const setUserProfile = (profileData) => ({
  type: types.SET_USER_PROFILE,
  payload: profileData
});

export const fetchUserProfile = (uid, options = {}) => {
  return async (dispatch) => {
    log('[fetchUserProfile] Fetching profile data for uid:', uid);
    
    try {
      // Get the current user from Firebase Authentication
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        log('[fetchUserProfile] No authenticated user found, aborting profile fetch');
        return { success: false, error: 'No authenticated user found' };
      }
      
      // Fetch account data from backend
      const response = await SPBackend.getAccount(currentUser);
      const { data } = response;
      log('[fetchUserProfile] Profile data retrieved: ', data);
      
      const { user, account, billing, tags, defaultUnit, defaultBuilding } = data;

      // Use batched action to update user profile in a single dispatch (reduces re-renders)
      // Note: billing is handled separately via setBilling to the billing slice
      // Note: tags are now handled via setTagsInTagsSlice to state.tags.tags (single source of truth)
      dispatch(setUserProfile({
        role: getUserRole(user),
        fullName: `${user.FirstName} ${user.LastName}`,
        isDemoMode: !!user.IsDemoMode,
        accounts: user.Accounts,
        accountId: account._id,
        companyName: account.CompanyName,
        IsPhoneVerified: !!user.IsPhoneVerified,
        Phone: user.Phone || '',
        accountSettings: account?.Settings || null,
        accountUsage: account?.Usage || null,
        defaultUnit: defaultUnit || null
      }));

      // Initialize tags in the tags slice (state.tags.tags) - single source of truth
      if (!options.skipTags && tags?.length > 0) {
        dispatch(setTagsInTagsSlice(tags));
      }

      // Separate dispatches for other slices (these don't affect user slice re-renders)
      if (defaultBuilding) {
        dispatch(setDefaultBuildingRedux(defaultBuilding));
      }

      // Update billing in the Redux store (separate billing slice)
      if (billing) {
        log('[fetchUserProfile] Billing data from backend:', billing);
        log('[fetchUserProfile] subscriptionId from billing:', billing.subscriptionId);
        log('[fetchUserProfile] subscriptionStatus from billing:', billing.subscriptionStatus);
        dispatch(setBilling(billing));
      } else {
        log('[fetchUserProfile] No billing data received from backend');
      }
      
      return { success: true, data };
    } catch (error) {
      log('[fetchUserProfile] Failed to fetch profile:', error);
      
      // Only show error message if this is a backend error, not auth state issue
      if (error.message !== 'No authenticated user found') {
        dispatch(setSnackMessage({
          message: 'Failed to load some user data',
          level: 'warning',
          duration: 5000
        }));
      }
      
      return { success: false, error: error.message };
    }
  };
};

export const login = (email: string, password: string) => {
  return async (dispatch: Function, getState: Function) => {
    log('[login] Starting authentication for email:', email);

    try {
      // Clear any stale token from previous user before authenticating
      clearTokenCache();

      log('[login] Attempting to sign in with Firebase');
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      log('[login] Firebase authentication successful');

      // Store auth data in localStorage
      localStorage.setItem('authUser', JSON.stringify({ email }));
      localStorage.setItem('loggedIn', 'true');

      // Update auth state (separate slice for Firebase auth data)
      dispatch(setAuth(user));

      // Reset user profile state (will be populated by fetchUserProfile)
      dispatch({ type: types.LOGIN });

      // Clear RTK Query caches to prevent stale data from previous user
      dispatch(feedApi.util.resetApiState());
      dispatch(tagsApi.util.resetApiState());

      // Fetch user profile immediately after login
      // This ensures profile is loaded before returning success
      log('[login] Fetching user profile...');
      const profileResult = await dispatch(fetchUserProfile(user.uid));

      if (!profileResult.success) {
        log('[login] Profile fetch failed:', profileResult.error);
        throw new Error(profileResult.error || 'Failed to load profile');
      }

      log('[login] Login complete with profile loaded');
      return { success: true, data: profileResult.data };
    } catch (err) {
      log('[login] Authentication failed:', err.message);

      // Handle authentication errors with user-friendly messages
      let errorMessage = 'Failed to login. Please check your credentials.';

      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        errorMessage = 'Invalid email or password. Please try again.';
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed login attempts. Please try again later.';
      } else if (err.code === 'auth/user-disabled') {
        errorMessage = 'Your account has been disabled. Please contact support.';
      } else if (err.message && !err.code) {
        // Profile fetch error or other non-Firebase error
        errorMessage = err.message;
      }

      dispatch(setSnackMessage({
        message: errorMessage,
        level: 'error',
        duration: 5000
      }));

      // Return failure status with error details
      return {
        success: false,
        error: errorMessage,
        errorCode: err.code
      };
    }
  };
};

export const logout = () => {
  return async (dispatch) => {
    try {
      log('[logout] Signing out from Firebase');

      // Clear token cache to prevent stale tokens on next login
      clearTokenCache();

      // Clean up localStorage
      localStorage.removeItem('loggedIn');
      localStorage.removeItem('authUser');
      localStorage.removeItem('token');
      localStorage.removeItem('email');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userFullName');

      // Sign out from Firebase
      await auth.signOut();
      log('[logout] Successfully signed out from Firebase');

      // Clear RTK Query caches to prevent stale data
      dispatch(feedApi.util.resetApiState());
      dispatch(tagsApi.util.resetApiState());
      log('[logout] RTK Query caches cleared');

      // Clear auth state (separate slice)
      dispatch(clearAuth());

      // Clear user profile state
      dispatch({ type: types.LOGOUT });

      return { success: true };
    } catch (error) {
      log('[logout] Error during logout:', error);

      // Still clear caches and dispatch logout actions
      dispatch(feedApi.util.resetApiState());
      dispatch(tagsApi.util.resetApiState());
      dispatch(clearAuth());
      dispatch({ type: types.LOGOUT });

      return { success: false, error: error.message };
    }
  };
};